/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { MouseEvent } from 'react';
// AER-1648: import renderable AntD controls from the `antd` barrel, never the
// deep `antd/lib/...` (CommonJS) paths - config-overrides.js rewrites barrel
// imports to `antd/es/*`, which is the module graph the app's ConfigProvider
// populates.
import { Tooltip } from 'antd';
import { get } from 'lodash';
import { AIcon } from '../icon';
import { Equipment } from '../interfaces/equipment';

const styles = {
  // The serial and its (i) share one inline-flex line box so the icon sits
  // immediately after the serial without changing the row height. The 10px gap
  // keeps the icon's (larger) hit box clear of the serial text itself.
  serialCell: `
    display: inline-flex;
    align-items: center;
    gap: 10px;
  `,
  // AER-1793: 28px hit box (7px padding + 14px glyph + 7px padding) behind the
  // icon. The glyph is 14px because AIcon renders its svg at 1em and the cell
  // inherits font-size: 14px - nothing here sizes it independently, so a change
  // to the row's font-size moves the box with it. The instinctive 44px
  // Apple/Material target is wrong here: the transfer table row is only ~38px
  // tall, so a 44px square spills into the rows above and below and
  // neighbouring rows' icons start triggering each other. 28px clears the 24x24
  // WCAG 2.5.8 minimum and stays inside the row with ~5px to spare top and
  // bottom. The matching negative margin collapses the padding back out of the
  // layout so the row does not shift.
  info: `
    display: inline-flex;
    padding: 7px;
    margin: -7px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.45);
  `,
};

let hoverQuery: MediaQueryList | null = null;

// Capability detection, not screen width - a small laptop window still has a
// real pointer. Passing `trigger={['hover', 'click']}` is the obvious move and
// it misbehaves on touch: iOS fires a synthetic hover on the first tap, so the
// tooltip opens on hover and the same tap immediately toggles it shut. Pick one
// trigger or the other, never both.
const canHover = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true;
  }
  if (!hoverQuery) {
    hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  }
  return hoverQuery.matches;
};

interface IEquipmentNameInfoProps {
  equipment: Equipment;
}

export const EquipmentNameInfo = ({ equipment }: IEquipmentNameInfoProps) => {
  const name = get(equipment, 'name');
  const equipmentSN = get(equipment, 'equipmentSN', '') as string;

  // Every row gets the icon, even when the name is just the serial - Daniel's
  // call on AER-1793 (2026-09-17). Showing it only for a distinct name left
  // most pages of these lists without a single icon, because unassigned
  // equipment is nearly always still named after its serial, and in testing
  // the feature looked missing. `name` is optional and defaults to the serial,
  // so a missing or blank name shows the serial. The typeof check matters:
  // lodash `get` hands back a stored `null` as-is, not the default.
  const title = typeof name === 'string' && name.trim() ? name : equipmentSN;

  return (
    <Tooltip
      title={title}
      trigger={canHover() ? 'hover' : 'click'}
      // Point at the middle of the trigger box rather than its edge, so a long
      // name (the bubble grows to antd's 250px max) still hangs its arrow off
      // the icon instead of drifting along the row.
      arrow={{ pointAtCenter: true }}
    >
      <span
        className="eq-info"
        css={css(styles.info)}
        // TableTransfer sets onRow.onClick to toggle row selection, so without
        // this a tap on the icon would also select/deselect the row.
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        <AIcon type="info-circle" />
      </span>
    </Tooltip>
  );
};

// Shared by the Edit Location and Edit Org equipment transfer lists - both
// screens carried a byte-identical copy of this block.
export const equipmentTransferColumns = [
  {
    title: 'Model ID',
    dataIndex: 'modelId',
  },
  {
    title: 'Serial Number',
    dataIndex: 'equipmentSN',
    render: (equipmentSN: string, equipment: Equipment) => (
      <span css={css(styles.serialCell)}>
        {equipmentSN}
        <EquipmentNameInfo equipment={equipment} />
      </span>
    ),
  },
];

// lodash `get` only falls back to its default for a missing key - a stored
// `null` comes back as `null` - so coerce before lowercasing. One null field
// would otherwise throw inside the Transfer's filter and unmount both lists.
const searchText = (item: Equipment, key: string): string => {
  const value = get(item, key);
  return value == null ? '' : `${value}`.toLowerCase();
};

// The search box matches on `name` as well as `modelId`/`equipmentSN`; the (i)
// tooltip above is what makes a name-only match explicable.
export const equipmentTransferFilterOption = (
  inputValue: string,
  item: Equipment
) => {
  const query = inputValue.toLowerCase();
  return ['name', 'modelId', 'equipmentSN'].some(
    (key) => searchText(item, key).indexOf(query) !== -1
  );
};
